// MATCH PARTY — memory pairs for 1–6 players, on one shared device (hot-seat) or online.
// Fixes vs. the old version: every pair is visually unique, both flipped cards stay
// visible to everyone before they turn back, bombs are removed after exploding,
// turn timer is enforced by the host, and hidden faces never reach clients.

import {
  activePlayers, bool, clampInt, oneOf, type BotPlan, type Engine, type TimedState,
} from "../engine";
import type { GameResultSummary, Lang, RoomCore } from "../types";

export interface MatchSettings {
  pairs: number; // 8 | 12 | 18 | 24
  theme: string; // card set id (client renders the faces)
  bombs: number; // 0 | 2 | 4
  turnTime: number; // seconds, 0 = no timer
}

export const MATCH_THEMES = ["supra", "animals", "party", "tbilisi", "emoji"] as const;
export const MATCH_FACES_PER_THEME = 24;

interface Card {
  id: string;
  face: number; // index into the theme's faces; -1 = bomb
  state: "down" | "up" | "matched" | "boom";
  by: string | null; // who matched / exploded it
}

type Phase = "turn" | "show" | "over";

export interface MatchState extends TimedState {
  phase: Phase;
  cards: Card[];
  order: string[];
  turn: number; // index into order
  flipped: string[]; // card ids currently face up (0..2)
  scores: Record<string, number>;
  pairsOf: Record<string, number>;
  streak: number; // consecutive matches in the current turn
  lastEvent: { kind: "match" | "miss" | "boom" | "timeout"; by: string; points: number; at: number } | null;
  seen: Record<string, number>; // card id -> face, remembered by bots (imperfectly)
  totalPairs: number;
}

const SHOW_MS = 1_300;
const BOOM_MS = 1_600;

const currentPlayer = (s: MatchState) => s.order[s.turn % s.order.length];

function setPhase(s: MatchState, phase: Phase, now: number, ms: number | null) {
  s.phase = phase;
  s.deadline = ms === null ? null : now + ms;
  s.phaseMs = ms ?? 0;
}

function startTurn(room: RoomCore<MatchSettings>, s: MatchState, now: number) {
  s.flipped = [];
  setPhase(s, "turn", now, room.settings.turnTime > 0 ? room.settings.turnTime * 1000 : null);
}

function passTurn(room: RoomCore<MatchSettings>, s: MatchState, now: number) {
  s.streak = 0;
  if (s.order.length) s.turn = (s.turn + 1) % s.order.length;
  startTurn(room, s, now);
}

function remainingPairs(s: MatchState) {
  return s.cards.filter((c) => c.face >= 0 && c.state !== "matched").length / 2;
}

export const matchEngine: Engine<MatchState, MatchSettings> = {
  id: "match",
  minPlayers: 1,
  maxPlayers: 6,
  supportsBots: true,

  defaults(_lang: Lang): MatchSettings {
    return { pairs: 12, theme: "supra", bombs: 0, turnTime: 0 };
  },

  sanitize(input: any, prev: MatchSettings): MatchSettings {
    const i = input && typeof input === "object" ? input : {};
    return {
      pairs: oneOf(clampInt(i.pairs, 6, 24, prev.pairs), [8, 12, 18, 24] as const, prev.pairs as 12),
      theme: oneOf(i.theme, MATCH_THEMES, prev.theme as (typeof MATCH_THEMES)[number]),
      bombs: oneOf(clampInt(i.bombs, 0, 4, prev.bombs), [0, 2, 4] as const, prev.bombs as 0),
      turnTime: oneOf(clampInt(i.turnTime, 0, 30, prev.turnTime), [0, 10, 15, 20] as const, prev.turnTime as 0),
    };
  },

  start(room, ctx) {
    const pairs = Math.min(room.settings.pairs, MATCH_FACES_PER_THEME);
    const faces = ctx.rng.shuffle(Array.from({ length: MATCH_FACES_PER_THEME }, (_, i) => i)).slice(0, pairs);
    const cards: Card[] = [];
    for (const f of faces) {
      cards.push({ id: ctx.rng.id(5), face: f, state: "down", by: null });
      cards.push({ id: ctx.rng.id(5), face: f, state: "down", by: null });
    }
    for (let b = 0; b < room.settings.bombs; b++) cards.push({ id: ctx.rng.id(5), face: -1, state: "down", by: null });
    const order = ctx.rng.shuffle(activePlayers(room).map((p) => p.id));
    const s: MatchState = {
      phase: "turn", deadline: null, phaseMs: 0,
      cards: ctx.rng.shuffle(cards), order, turn: 0, flipped: [],
      scores: Object.fromEntries(order.map((id) => [id, 0])),
      pairsOf: Object.fromEntries(order.map((id) => [id, 0])),
      streak: 0, lastEvent: null, seen: {}, totalPairs: pairs,
    };
    startTurn(room, s, ctx.now);
    return s;
  },

  act(room, s, actorId, a, ctx) {
    if (!a || typeof a !== "object") return "bad_input";
    if (a.t !== "flip") return "bad_input";
    if (s.phase !== "turn") return "bad_phase";
    const who = currentPlayer(s);
    // On a shared device (local room) whoever holds it plays for the current player.
    if (!room.local && actorId !== who) return "not_your_turn";
    const card = s.cards.find((c) => c.id === a.card);
    if (!card || card.state !== "down") return "bad_input";
    card.state = "up";
    s.flipped.push(card.id);
    s.seen[card.id] = card.face;
    if (card.face < 0) {
      // BOOM: lose points, bomb is gone, the other flipped card turns back, turn passes.
      card.state = "boom";
      card.by = who;
      const loss = Math.min(s.scores[who] ?? 0, 150);
      s.scores[who] = (s.scores[who] ?? 0) - loss;
      s.lastEvent = { kind: "boom", by: who, points: -loss, at: ctx.now };
      setPhase(s, "show", ctx.now, BOOM_MS);
      return s;
    }
    if (s.flipped.length === 2) {
      setPhase(s, "show", ctx.now, SHOW_MS);
    } else if (room.settings.turnTime > 0) {
      setPhase(s, "turn", ctx.now, room.settings.turnTime * 1000);
    }
    return s;
  },

  tick(room, s, ctx) {
    const who = currentPlayer(s);
    if (s.phase === "turn") {
      // turn timer ran out
      for (const id of s.flipped) {
        const c = s.cards.find((x) => x.id === id);
        if (c && c.state === "up") c.state = "down";
      }
      s.lastEvent = { kind: "timeout", by: who, points: 0, at: ctx.now };
      passTurn(room, s, ctx.now);
      return s;
    }
    if (s.phase === "show") {
      const flipped = s.flipped.map((id) => s.cards.find((c) => c.id === id)!).filter(Boolean);
      const boom = flipped.find((c) => c.state === "boom");
      if (boom) {
        for (const c of flipped) if (c.state === "up") c.state = "down";
        passTurn(room, s, ctx.now);
      } else if (flipped.length === 2 && flipped[0].face === flipped[1].face) {
        s.streak += 1;
        const points = 100 + (s.streak - 1) * 50;
        for (const c of flipped) { c.state = "matched"; c.by = who; }
        s.scores[who] = (s.scores[who] ?? 0) + points;
        s.pairsOf[who] = (s.pairsOf[who] ?? 0) + 1;
        s.lastEvent = { kind: "match", by: who, points, at: ctx.now };
        if (remainingPairs(s) === 0) {
          s.flipped = [];
          setPhase(s, "over", ctx.now, null);
        } else {
          startTurn(room, s, ctx.now); // same player goes again
        }
      } else {
        for (const c of flipped) c.state = "down";
        s.lastEvent = { kind: "miss", by: who, points: 0, at: ctx.now };
        passTurn(room, s, ctx.now);
      }
      return s;
    }
    s.deadline = null;
    return s;
  },

  onLeave(room, s, playerId, ctx) {
    const idx = s.order.indexOf(playerId);
    if (idx < 0) return s;
    const wasTurn = currentPlayer(s) === playerId;
    s.order.splice(idx, 1);
    if (!s.order.length) { setPhase(s, "over", ctx.now, null); return s; }
    if (idx < s.turn) s.turn -= 1;
    s.turn = s.turn % s.order.length;
    if (wasTurn && s.phase !== "over") {
      for (const id of s.flipped) {
        const c = s.cards.find((x) => x.id === id);
        if (c && c.state === "up") c.state = "down";
      }
      s.streak = 0;
      startTurn(room, s, ctx.now);
    }
    return s;
  },

  onJoin(_room, s, playerId) {
    if (!s.order.includes(playerId)) {
      s.order.push(playerId);
      s.scores[playerId] = 0;
      s.pairsOf[playerId] = 0;
    }
    return s;
  },

  view(room, s, viewerId) {
    return {
      phase: s.phase,
      deadline: s.deadline,
      phaseMs: s.phaseMs,
      theme: room.settings.theme,
      cards: s.cards.map((c) => ({
        id: c.id,
        state: c.state,
        face: c.state === "down" ? null : c.face,
        by: c.by,
      })),
      order: s.order,
      current: currentPlayer(s),
      myTurn: room.local || (!!viewerId && currentPlayer(s) === viewerId),
      flipped: s.flipped,
      scores: s.scores,
      pairsOf: s.pairsOf,
      streak: s.streak,
      lastEvent: s.lastEvent,
      totalPairs: s.totalPairs,
      left: remainingPairs(s),
    };
  },

  bot(room, s, botId, ctx): BotPlan | null {
    if (s.phase !== "turn" || currentPlayer(s) !== botId) return null;
    const down = s.cards.filter((c) => c.state === "down");
    if (!down.length) return null;
    const memory = 0.72; // chance to remember a seen card
    const known = new Map<number, string[]>();
    for (const c of down) {
      const f = s.seen[c.id];
      if (f !== undefined && f >= 0 && ctx.rng.next() < memory) {
        known.set(f, [...(known.get(f) ?? []), c.id]);
      }
    }
    let pick: string;
    if (s.flipped.length === 1) {
      const first = s.cards.find((c) => c.id === s.flipped[0])!;
      const partner = known.get(first.face)?.find((id) => id !== first.id);
      const unknown = down.filter((c) => s.seen[c.id] === undefined);
      pick = partner ?? (unknown.length ? ctx.rng.pick(unknown).id : ctx.rng.pick(down).id);
    } else {
      const pair = [...known.values()].find((ids) => ids.length >= 2);
      const unknown = down.filter((c) => s.seen[c.id] === undefined);
      pick = pair ? pair[0] : unknown.length ? ctx.rng.pick(unknown).id : ctx.rng.pick(down).id;
    }
    return { key: `flip:${s.turn}:${s.flipped.length}:${s.cards.filter((c) => c.state !== "down").length}`, delay: 700 + ctx.rng.int(700), action: { t: "flip", card: pick } };
  },

  isOver(s) {
    return s.phase === "over";
  },

  summary(room, s): GameResultSummary {
    const players = room.players
      .filter((p) => s.order.includes(p.id))
      .map((p) => ({ id: p.id, name: p.name, score: s.scores[p.id] ?? 0, userId: p.userId ?? null, isBot: p.isBot }));
    const max = Math.max(0, ...players.map((p) => p.score));
    return { game: "match", lang: room.lang, players, winnerIds: players.filter((p) => p.score === max).map((p) => p.id) };
  },
};

export const _bool = bool;
